import { z } from "zod";

import { BaseLabelValueValuesSchema } from "@/modules/common/schemas/sharedSchemas";

const QuestionTypesSchema = z.enum([
  "dummy",
  "text",
  "boolean",
  "number",
  "audio",
  "photo",
  "video",
  "select",
  "sort",
  "rating",
  "matrix",
]);
export const QuestionTypes = QuestionTypesSchema.enum;
export const QuestionTypesOptions = QuestionTypesSchema.options;

const MatrixFieldTypesSchema = z.enum([
  "text",
  "number",
  "select",
  "boolean",
  "rating",
]);
export const MatrixFieldTypes = MatrixFieldTypesSchema.enum;
export const MatrixFieldTypesOptions = MatrixFieldTypesSchema.options;

const RatingThemesSchema = z.enum([
  "default",
  "bars",
  "drops",
  "likert",
  "skulls",
  "smileys",
  "stars",
]);
export const RatingThemes = RatingThemesSchema.enum;
export const RatingThemesOptions = RatingThemesSchema.options;

const BooleanThemesSchema = z.enum(["regular", "radio", "checkbox", "toggle"]);
export const BooleanThemes = BooleanThemesSchema.enum;
export const BooleanThemesOptions = BooleanThemesSchema.options;

const MinMaxSchema = z.object({
  minimum: z.coerce
    .number({
      error: "Minimum must be a number",
    })
    .gte(0)
    .int()
    .optional(),
  maximum: z.coerce
    .number({
      error: "Maximum must be a number",
    })
    .gte(1)
    .int()
    .optional(),
});

const OptionsModeEnum = z.enum(["tag", "list"]);
export const OptionsModeTypes = OptionsModeEnum.enum;

const OptionAttrSchema = z.object({
  value: z.string().trim().min(1, {
    error: "Required",
  }),
});

const BaseOptionsSchema = z.object({
  options_mode: OptionsModeEnum.prefault(OptionsModeTypes.list),
  options: z.array(OptionAttrSchema).optional(),
  options_tag: BaseLabelValueValuesSchema.optional(),
  randomize: z.boolean().prefault(false),
});

const NumberSchema = z.enum(["i", "f"], {
  error: "Number type is required",
});

const CurrencySchema = z.string().optional();

const BaseQuestionSchema = z.object({
  q: z.string().trim().min(1, {
    error: "Question is required",
  }),
  anchored: z.boolean().prefault(false),
  required: z.boolean().prefault(false),
});

const DummyButtonTypesEnum = z.enum(["clipboard", "deeplink"]);
export const DummyButtonTypes = DummyButtonTypesEnum.enum;

export const DummyButtonSchema = z.object({
  label: z.string(),
  color: z.string(),
  type: DummyButtonTypesEnum,
  clipboard: z.string().optional(),
  google_id: z.string().optional(),
  apple_id: z.string().optional(),
  google_deeplink: z.string().optional(),
  apple_deeplink: z.string().optional(),
});

export const QuestionDummySchema = BaseQuestionSchema.extend({
  type: z.literal(QuestionTypes.dummy),
  buttons: z.array(DummyButtonSchema),
});

export const QuestionTextSchema = BaseQuestionSchema.extend({
  type: z.literal(QuestionTypes.text),
  use_mask: z.boolean().prefault(false),
  mask: z.string().optional(),
  multiline: z.boolean().optional(),
  maximum: z.coerce
    .number({
      error: "Maximum Characters Limit must be a number",
    })
    .optional(),
  word_limit: z.boolean().optional(),
  minimum_words: z.coerce
    .number({
      error: "Maximum Characters Limit must be a number",
    })
    .optional(),
  maximum_words: z.coerce
    .number({
      error: "Maximum Characters Limit must be a number",
    })
    .optional(),
});

export const QuestionBooleanSchema = BaseQuestionSchema.extend({
  type: z.literal(QuestionTypes.boolean),
});

export const QuestionNumberSchema = BaseQuestionSchema.extend({
  type: z.literal(QuestionTypes.number),
  number_type: NumberSchema,
  currency: CurrencySchema,
  minimum: z.coerce
    .number({
      error: "Minimum must be a number",
    })
    .optional(),
  maximum: z.coerce
    .number({
      error: "Maximum must be a number",
    })
    .optional(),
});

export const QuestionAudioSchema = BaseQuestionSchema.extend({
  type: z.literal(QuestionTypes.audio),
  duration: z.coerce
    .number()
    .min(5, {
      error: "Duration must be between 5 and 180",
    })
    .max(180, {
      error: "Duration must be between 5 and 180",
    })
    .prefault(5),
  format: z.string().optional(),
  quality: z.coerce.number().min(1).max(5).optional().prefault(4),
});

export const QuestionPhotoSchema = BaseQuestionSchema.extend({
  type: z.literal(QuestionTypes.photo),
  quality: z.coerce.number().prefault(6),
  gallery: z.boolean().prefault(false),
});

export const QuestionVideoSchema = BaseQuestionSchema.extend({
  type: z.literal(QuestionTypes.video),
  duration: z.coerce
    .number()
    .min(5, {
      error: "Duration must be between 5 and 180",
    })
    .max(180, {
      error: "Duration must be between 5 and 180",
    })
    .prefault(5),
  gallery: z.boolean().prefault(false),
});

export const QuestionSelectSchema = BaseQuestionSchema.extend({
  type: z.literal(QuestionTypes.select),
  auto_exclusive: z.string().optional(),
  auto_other: z.string().optional(),
  minimum: z.coerce
    .number({
      error: "Minimum must be a number",
    })
    .optional(),
  maximum: z.preprocess(
    (val) => (val === "" ? undefined : val),
    z.coerce
      .number({
        error: "Maximum must be a number",
      })
      .gte(0)
  ),
}).extend(BaseOptionsSchema.shape);

export const QuestionSortSchema = BaseQuestionSchema.extend({
  type: z.literal("sort"),
}).extend(BaseOptionsSchema.shape);

export const QuestionRatingSchema = BaseQuestionSchema.extend({
  type: z.literal(QuestionTypes.rating),
  jackets: RatingThemesSchema,
  labels: z.tuple([z.string(), z.string(), z.string()]),
}).extend(MinMaxSchema.shape);

export const QuestionMatrixSchema = BaseQuestionSchema.extend({
  type: z.literal(QuestionTypes.matrix),
  currency: CurrencySchema,

  rows_mode: OptionsModeEnum.default(OptionsModeTypes.list),
  rows: z.array(OptionAttrSchema).optional(),
  rows_tag: BaseLabelValueValuesSchema.optional(),
  rows_randomize: z.boolean().prefault(false).optional(),

  cols_mode: OptionsModeEnum.default(OptionsModeTypes.list),
  cols: z.array(OptionAttrSchema).optional(),
  cols_tag: BaseLabelValueValuesSchema.optional(),
  cols_randomize: z.boolean().prefault(false).optional(),

  field_type: MatrixFieldTypesSchema,

  maximum: z.coerce
    .number({
      error: "Maximum Characters Limit must be a number",
    })
    .optional(),
  word_limit: z.boolean().prefault(false),
  minimum_words: z.coerce
    .number({
      error: "Maximum Characters Limit must be a number",
    })
    .gte(0)
    .optional(),
  maximum_words: z.coerce
    .number({
      error: "Maximum Characters Limit must be a number",
    })
    .gte(0)
    .optional(),

  jackets: RatingThemesSchema.optional(),
  number_type: NumberSchema.optional(),
  boolean_theme: z.coerce.number().optional(),
  labels: z
    .tuple([
      z.string().optional(),
      z.string().optional(),
      z.string().optional(),
    ])
    .optional(),
  minimum: z.coerce
    .number({
      error: "Minimum must be a number",
    })
    .optional(),
}).extend(BaseOptionsSchema.shape);

export const QuestionSchema = z.discriminatedUnion(
  "type",
  [
    QuestionDummySchema,
    QuestionTextSchema,
    QuestionBooleanSchema,
    QuestionNumberSchema,
    QuestionAudioSchema,
    QuestionPhotoSchema,
    QuestionVideoSchema,
    QuestionSelectSchema,
    QuestionSortSchema,
    QuestionRatingSchema,
    QuestionMatrixSchema,
  ],
  {
    error: (issue) => {
      if (issue.code) {
        return "Type is required";
      }
    },
  }
);

function validationSortSelectOptions(
  options_mode: string,
  options_tag: { value: string; values: string[]; label: string } | undefined,
  options: { value: string }[] | undefined,
  ctx: z.RefinementCtx
) {
  if (options_mode === OptionsModeTypes.tag) {
    if (!options_tag) {
      ctx.addIssue({
        code: "custom",
        message: "Select an option tag",
        path: ["options_tag"],
      });
    }
  } else {
    if (!options || options?.length === 0) {
      ctx.addIssue({
        code: "custom",
        message: "Add at least one option",
        path: ["options"],
      });
    }

    const seen = new Set();
    const duplicatedIndexes: number[] = [];
    const hasDuplicates = (options ?? []).some((v, idx) => {
      if (seen.has(v.value.trim())) {
        duplicatedIndexes.push(idx);
        return true;
      }
      seen.add(v.value.trim());
      return false;
    });
    if (hasDuplicates) {
      duplicatedIndexes.forEach((v) => {
        ctx.addIssue({
          code: "custom",
          message: "Duplicated value",
          path: ["options", v, "value"],
        });
      });
    }
  }
}

const validateMinimumMaximumTypeNumberInteger = function (
  ctx: z.RefinementCtx,
  minimum: number | undefined,
  maximum: number | undefined
) {
  const minimumIntValidation = z.coerce
    .number()
    .gte(1)
    .int()
    .optional()
    .safeParse(minimum);
  if (!minimumIntValidation.success) {
    ctx.addIssue({
      code: "custom",
      message: minimumIntValidation.error.issues[0].message,
      path: ["minimum"],
    });
  }
  const maximumIntValidation = z.coerce
    .number()
    .gte(1)
    .lte(2000000000)
    .int()
    .optional()
    .safeParse(maximum);
  if (!maximumIntValidation.success) {
    ctx.addIssue({
      code: "custom",
      message: maximumIntValidation.error.issues[0].message,
      path: ["maximum"],
    });
  }
};

const validateMinimumMaximumTypeNumberFloat = function (
  ctx: z.RefinementCtx,
  minimum: number | undefined,
  maximum: number | undefined
) {
  const minimumFloatValidation = z.coerce
    .number()
    .gte(1)
    .optional()
    .safeParse(minimum);
  if (!minimumFloatValidation.success) {
    ctx.addIssue({
      code: "custom",
      message: minimumFloatValidation.error.issues[0].message,
      path: ["minimum"],
    });
  }
  const maximumFloatValidation = z
    .number()
    .gte(1)
    .lte(2000000000)
    .optional()
    .safeParse(maximum);
  if (!maximumFloatValidation.success) {
    ctx.addIssue({
      code: "custom",
      message: maximumFloatValidation.error.issues[0].message,
      path: ["maximum"],
    });
  }
};

const QuestionWithValidationSchema = QuestionSchema.superRefine((val, ctx) => {
  switch (val.type) {
    case QuestionTypes.select: {
      validationSortSelectOptions(
        val.options_mode,
        val.options_tag,
        val.options,
        ctx
      );
      if (val.options && val.minimum && val.options?.length < val.minimum) {
        ctx.addIssue({
          code: "custom",
          message: "Minimum must be less than total number of options",
          path: ["minimum"],
        });
      }
      if (val.options && val.maximum && val.options?.length < val.maximum) {
        ctx.addIssue({
          code: "custom",
          message: "Maximum value cannot be greater than number of options",
          path: ["maximum"],
        });
      }
      if (val.auto_exclusive && typeof val.auto_exclusive === "string") {
        if (val.auto_exclusive.trim().length === 0) {
          ctx.addIssue({
            code: "custom",
            path: ["auto_exclusive"],
            message: "Auto Exclusive cannot contain only space.",
          });
        }
        val.auto_exclusive = val.auto_exclusive.trim();
      }
      if (val.auto_other && typeof val.auto_other === "string") {
        if (val.auto_other.trim().length === 0) {
          ctx.addIssue({
            code: "custom",
            path: ["auto_other"],
            message: "Auto Other cannot contain only space.",
          });
        }
        val.auto_other = val.auto_other.trim();
      }
      break;
    }
    case QuestionTypes.sort: {
      validationSortSelectOptions(
        val.options_mode,
        val.options_tag,
        val.options,
        ctx
      );
      break;
    }
    case QuestionTypes.number: {
      if (val.number_type === "i") {
        validateMinimumMaximumTypeNumberInteger(ctx, val.minimum, val.maximum);
      } else if (val.number_type === "f") {
        validateMinimumMaximumTypeNumberFloat(ctx, val.minimum, val.maximum);
      }
      if (val.minimum && val.maximum && val.minimum > val.maximum) {
        ctx.addIssue({
          code: "custom",
          message: "Maximum must be greater than minimum",
          path: ["maximum"],
        });
      }
      break;
    }
    case QuestionTypes.matrix: {
      if (val.rows_mode === OptionsModeTypes.tag) {
        if (!val.rows_tag) {
          ctx.addIssue({
            code: "custom",
            message: "Select a row tag",
            path: ["rows_tag"],
          });
        }
      } else {
        if (!val.rows || val.rows?.length === 0) {
          ctx.addIssue({
            code: "custom",
            message: "Add at least one row",
            path: ["rows"],
          });
        }

        const seen = new Set();
        const duplicatedIndexes: number[] = [];
        const hasDuplicates = (val.rows ?? []).some((v, idx) => {
          if (seen.has(v.value)) {
            duplicatedIndexes.push(idx);
            return true;
          }
          seen.add(v.value);
          return false;
        });
        if (hasDuplicates) {
          duplicatedIndexes.forEach((v) => {
            ctx.addIssue({
              code: "custom",
              message: "Duplicated value",
              path: ["rows", v, "value"],
            });
          });
        }
      }

      if (val.cols_mode === OptionsModeTypes.tag) {
        if (!val.cols_tag) {
          ctx.addIssue({
            code: "custom",
            message: "Select a col tag",
            path: ["cols_tag"],
          });
        }
      } else {
        if (!val.cols || val.cols?.length === 0) {
          ctx.addIssue({
            code: "custom",
            message: "Add at least one column",
            path: ["cols"],
          });
        }

        const seen = new Set();
        const duplicatedIndexes: number[] = [];
        const hasDuplicates = (val.cols ?? []).some((v, idx) => {
          if (seen.has(v.value)) {
            duplicatedIndexes.push(idx);
            return true;
          }
          seen.add(v.value);
          return false;
        });
        if (hasDuplicates) {
          duplicatedIndexes.forEach((v) => {
            ctx.addIssue({
              code: "custom",
              message: "Duplicated value",
              path: ["cols", v, "value"],
            });
          });
        }
      }

      switch (val.field_type) {
        case MatrixFieldTypes.text: {
          break;
        }
        case MatrixFieldTypes.number: {
          if (!val.number_type) {
            ctx.addIssue({
              code: "custom",
              message: "Select a number type",
              path: ["number_type"],
            });
          }

          if (val.number_type === "i") {
            validateMinimumMaximumTypeNumberInteger(
              ctx,
              val.minimum,
              val.maximum
            );
          } else if (val.number_type === "f") {
            validateMinimumMaximumTypeNumberFloat(
              ctx,
              val.minimum,
              val.maximum
            );
          }
          if (val.minimum && val.maximum && val.minimum > val.maximum) {
            ctx.addIssue({
              code: "custom",
              message: "Maximum must be greater than minimum",
              path: ["maximum"],
            });
          }
          break;
        }
        case MatrixFieldTypes.select: {
          if (val.options_mode === OptionsModeTypes.tag) {
            if (!val.options_tag) {
              ctx.addIssue({
                code: "custom",
                message: "Select an option tag",
                path: ["options_tag"],
              });
            }
          } else {
            if (!val.options || val.options?.length === 0) {
              ctx.addIssue({
                code: "custom",
                message: "Add at least one row",
                path: ["options"],
              });
            }

            const seen = new Set();
            const duplicatedIndexes: number[] = [];
            const hasDuplicates = (val.options ?? []).some((v, idx) => {
              if (seen.has(v.value)) {
                duplicatedIndexes.push(idx);
                return true;
              }
              seen.add(v.value);
              return false;
            });
            if (hasDuplicates) {
              duplicatedIndexes.forEach((v) => {
                ctx.addIssue({
                  code: "custom",
                  message: "Duplicated value",
                  path: ["options", v, "value"],
                });
              });
            }
          }
          break;
        }
        case MatrixFieldTypes.boolean: {
          const booleanThemesIndexes = BooleanThemesOptions.map(
            (_, index) => index
          );
          if (
            val.boolean_theme == null ||
            !booleanThemesIndexes.includes(val.boolean_theme)
          ) {
            ctx.addIssue({
              code: "custom",
              message: "Select a theme",
              path: ["boolean_theme"],
            });
          }
          break;
        }
        case MatrixFieldTypes.rating: {
          if (!val.jackets) {
            ctx.addIssue({
              code: "custom",
              message: "Select a theme",
              path: ["jackets"],
            });
          }
          break;
        }
      }
      break;
    }
  }
});

export const CreateBeaconSchema = z.object({
  name: z
    .string()
    .max(100)
    .trim()
    .nonempty({
      error: "Name is required",
    })
    .refine(
      (value) => /^[A-Z0-9_]+$/.test(value),
      "Only upper case letters, numbers and underscores are allowed"
    ),
  customer: z
    .object(
      {
        value: z.string(),
        label: z.string(),
      },
      {
        error: "Customer is required",
      }
    )
    .refine((val) => val != null && val?.value != "", {
      error: "Customer is required",
    }),
  extra: z.array(
    z.object({
      value: z.string(),
      label: z.string(),
    })
  ),
  tags: z.array(
    z.object({
      value: z.string(),
      label: z.string(),
    })
  ),
  question: QuestionWithValidationSchema,
});

// ------------------------------------------------------------------------------
// Types exported

export type Question = z.infer<typeof QuestionSchema>;
export type QuestionDummy = z.infer<typeof QuestionDummySchema>;
export type DummyButton = z.infer<typeof DummyButtonSchema>;
export type QuestionText = z.infer<typeof QuestionTextSchema>;
export type QuestionBoolean = z.infer<typeof QuestionBooleanSchema>;
export type QuestionNumber = z.infer<typeof QuestionNumberSchema>;
export type QuestionAudio = z.infer<typeof QuestionAudioSchema>;
export type QuestionPhoto = z.infer<typeof QuestionPhotoSchema>;
export type QuestionVideo = z.infer<typeof QuestionVideoSchema>;
export type QuestionSelect = z.infer<typeof QuestionSelectSchema>;
export type QuestionSort = z.infer<typeof QuestionSortSchema>;
export type QuestionRating = z.infer<typeof QuestionRatingSchema>;
export type QuestionMatrix = z.infer<typeof QuestionMatrixSchema>;

export type CreateBeacon = z.infer<typeof CreateBeaconSchema>;
